import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../ui/UIElements/Icon";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import { useConversation } from "@finitless/shared/hooks";
import ConversationChat from "../../ui/Chat/ConversationChat";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";
import { useTranslation } from "react-i18next";

const ConversationDetailPage = ({ initialConversation = null }) => {
  const { t } = useTranslation("conversations");
  const { id } = useParams();
  const conversationId = parseInt(id, 10);
  const navigate = useNavigate();
  const { parseDate, parsePrice } = useGlobalUtils();
  const {
    data: fetchedConversation,
    isLoading: conversationLoading,
    error: conversationError,
  } = useConversation(conversationId, {
    params:
      "id,status,status_reason,type,user(name,lastname,country_phone_code,cellphone),agent,messages_count,user_email,user_cellphone,last_message_at,created_at,public_data,private_data,order(summary),usage",
  });
  const conversation = fetchedConversation || initialConversation;

  const handleBackToConversations = () => navigate("/analyze/conversations");
  const handleViewOrder = () => {
    if (!conversation?.order?.id) return;
    navigate(`/orders/${conversation.order.id}`);
  };

  const formatTimestamp = (timestamp) =>
    parseDate(timestamp, {
      formatTime: "12",
      outputFormat: "MMM d, h:mm a",
      fallbackText: t("chat.fallbackNoTime"),
    });

  const getStatusStyle = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "closed":
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      case "writing":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getStatusReasonStyle = (status_reason) => {
    switch (status_reason) {
      case "completed":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "abandoned":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "failed":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  if (conversationLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
          <p className="text-gray-400">{t("detail.states.loading")}</p>
        </div>
      </div>
    );
  }

  if (conversationError || (!conversationLoading && !conversation)) {
    return (
      <div className="py-12 text-center">
        <Icon
          name="MessageCircle"
          className="mx-auto mb-4 h-12 w-12 text-gray-400"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300">
          {t("detail.states.notFoundTitle")}
        </h3>
        <p className="mt-1 text-sm text-gray-400">
          {conversationError?.message || t("detail.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToConversations}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          {t("detail.actions.backToConversations")}
        </button>
      </div>
    );
  }

  const userName = [conversation?.user?.name, conversation?.user?.lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  const countryPhoneCode = conversation?.user?.country_phone_code
    ? `+${conversation?.user?.country_phone_code}`
    : "";
  const userContact = [countryPhoneCode, conversation?.user?.cellphone]
    .filter(Boolean)
    .join(" ")
    .trim();
  const {
    type: conversationType,
    status,
    messages_count: messagesCount,
    last_message_at: lastMessageAt,
  } = conversation || {};
  const statusLabel = t(`status.${status}`, {
    defaultValue: status || t("detail.labels.unknown"),
  });
  const statusReasonLabel = t(`statusReason.${conversation?.status_reason}`, {
    defaultValue: conversation?.status_reason,
  });
  const formattedLastMessage = formatTimestamp(lastMessageAt);
  const formattedStartedAt = formatTimestamp(conversation?.created_at);

  const breadcrumbItems = [
    {
      label: t("header.title"),
      path: "/analyze/conversations",
      icon: "MessageCircle",
    },
    {
      label: t("detail.header.conversationNumber", { id: conversationId }),
      active: true,
      disabled: true,
    },
  ];

  const channelTypeMap = {
    whatsapp: "whatsapp",
    "whatsapp:chat": "whatsapp",
    "ordering:chat": "chat",
    "finitless:chat": "chat",
    ordering: "chat",
  };

  const getChannelData = (type) => {
    const channel = channelTypeMap[type] || "default";

    if (channel === "whatsapp") {
      return {
        label: t("detail.channels.whatsapp"),
        bgClass: "bg-green-500",
      };
    }

    if (channel === "chat") {
      return {
        label: t("detail.channels.webChat"),
        bgClass: "bg-blue-500",
      };
    }

    return {
      label: t("detail.channels.web"),
      bgClass: "bg-gray-500",
    };
  };

  const channelData = getChannelData(conversationType);

  const getConversationIcon = (type) => {
    const channel = channelTypeMap[type] || "default";

    switch (channel) {
      case "whatsapp":
        return (
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${channelData.bgClass}`}
          >
            <svg
              className="h-6 w-6 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          </div>
        );
      case "chat":
        return (
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${channelData.bgClass}`}
          >
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-500">
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        );
    }
  };

  const contextRows = [
    {
      label: t("detail.labels.participant"),
      value: userName || t("notifications.unknownUser"),
    },
    {
      label: t("detail.labels.contact"),
      value:
        userContact || conversation?.user_email || t("detail.labels.unknown"),
    },
    {
      label: t("detail.labels.conversationId"),
      value: conversationId,
    },
    conversation?.order?.id && {
      label: t("detail.labels.orderId"),
      value: conversation.order.id,
    },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="rounded-lg border border-gray-800 bg-gray-900">
        <div className="border-b border-gray-800 p-4 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-800 sm:h-16 sm:w-16">
                {getConversationIcon(conversationType)}
              </div>

              <div className="min-w-0">
                <p className="text-sm text-gray-400">
                  {t("detail.header.conversationNumber", {
                    id: conversationId,
                  })}
                </p>
                <h1 className="truncate text-2xl font-bold text-white">
                  {userName || t("notifications.unknownUser")}
                </h1>
                {userContact && (
                  <p className="text-sm text-gray-400">{userContact}</p>
                )}

                {!userContact && conversation?.user_email && (
                  <p className="text-sm text-gray-400">
                    {conversation.user_email}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-gray-700 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-300">
                    {channelData.label}
                  </span>

                  <span className="rounded-full border border-gray-700 bg-gray-800 px-3 py-1 text-xs font-medium text-gray-300">
                    {t("detail.labels.totalMessages")}: {messagesCount || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 lg:items-end">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleBackToConversations}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 transition-colors hover:border-gray-600 hover:bg-gray-700"
                >
                  <Icon name="ArrowLeft" className="h-4 w-4" />
                  {t("detail.actions.backToConversations")}
                </button>

                {!!conversation?.order?.id && (
                  <button
                    onClick={handleViewOrder}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <Icon name="ShoppingBag" className="h-4 w-4" />
                    {t("detail.actions.viewOrder")}
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-400">
                  {t("detail.labels.status")}:
                </span>
                <span
                  className={`rounded-full border px-3 py-1 text-sm font-medium capitalize ${getStatusStyle(status)}`}
                >
                  {statusLabel}
                </span>
              </div>

              {conversation?.status_reason && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-400">
                    {t("detail.labels.reason")}:
                  </span>
                  <span
                    className={`rounded-full border px-3 py-1 text-sm font-medium capitalize ${getStatusReasonStyle(conversation.status_reason)}`}
                  >
                    {statusReasonLabel}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {conversation?.status_reason === "completed" &&
              conversation?.usage?.roi != null &&
              conversation.usage.roi >= 0 && (
                <div className="rounded-lg border border-gray-800 bg-gray-800/50 p-3">
                  <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                    {t("card.labels.roi")}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {t("card.labels.roiValue", {
                      value: conversation.usage.roi.toFixed(2),
                    })}
                  </p>
                </div>
              )}
            {conversation?.usage?.cost != null &&
              conversation.usage.cost >= 0 && (
                <div className="rounded-lg border border-gray-800 bg-gray-800/50 p-3">
                  <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                    {t("detail.labels.cost")}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">
                    {parsePrice(conversation.usage.cost)}
                  </p>
                </div>
              )}
            {!!conversation?.usage?.average_response_time_ms && (
              <div className="rounded-lg border border-gray-800 bg-gray-800/50 p-3">
                <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                  {t("card.labels.responseTime")}
                </p>
                <p className="mt-1 text-sm font-semibold text-white">
                  {convertTime(
                    conversation.usage.average_response_time_ms,
                    "ms",
                    "s",
                    { decimals: 1, includeUnit: true },
                  )}
                </p>
              </div>
            )}
            <div className="rounded-lg border border-gray-800 bg-gray-800/50 p-3">
              <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                {t("detail.labels.startedAt")}
              </p>
              <p className="mt-1 text-sm font-semibold text-white">
                {formattedStartedAt}
              </p>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-800/50 p-3">
              <p className="text-xs font-medium tracking-wide text-gray-400 uppercase">
                {t("detail.labels.lastMessage")}
              </p>
              <p className="mt-1 text-sm font-semibold text-white">
                {formattedLastMessage}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 p-4 sm:p-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <section className="rounded-lg border border-gray-800 bg-gray-800/30">
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3 sm:px-5">
              <h3 className="text-lg font-semibold text-white">
                {t("detail.sections.messages")}
              </h3>
              <span className="rounded-full border border-gray-700 bg-gray-800 px-2 py-1 text-xs font-medium text-gray-300">
                {messagesCount || 0}
              </span>
            </div>

            <div className="p-3 sm:p-4">
              <ConversationChat
                conversationId={conversationId}
                maxHeight="h-[62vh]"
                showScrollButton={true}
                enablePagination={true}
              />
            </div>
          </section>

          <aside className="space-y-4">
            <div className="rounded-lg border border-gray-800 bg-gray-800/30 p-4 sm:p-5">
              <h4 className="text-sm font-semibold tracking-wide text-gray-200 uppercase">
                {t("detail.sections.context")}
              </h4>

              <dl className="mt-4 space-y-3">
                {contextRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start justify-between gap-3"
                  >
                    <dt className="text-xs text-gray-400">{row.label}</dt>
                    <dd className="text-right text-sm font-medium text-white">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {conversation?.summary && (
              <div className="rounded-lg border border-gray-800 bg-gray-800/30 p-4 sm:p-5">
                <h4 className="text-sm font-semibold tracking-wide text-gray-200 uppercase">
                  {t("detail.sections.summary")}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-gray-300">
                  {conversation.summary}
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetailPage;
